import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useNotificationStore, SystemNotification } from "@/store/notificationStore";
import { NotificationType, getNotificationCategory } from "@/types/notifications";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { useAuthStore } from "@/store/authStore";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Bell } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
    NotificationHeader,
    NotificationTabs,
    NotificationContent
} from "@/components/notifications";

const NotificationsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { selectedStoreId } = useStoreManagementStore();
  const {
    notifications,
    unreadCount,
    isLoading,
    error,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    setupRealtimeSubscription,
    getUnreadCount,
  } = useNotificationStore();

  const [activeTab, setActiveTab] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    if (selectedStoreId && user) {
      fetchNotifications(selectedStoreId);
      const unsubscribe = setupRealtimeSubscription(selectedStoreId);
      return unsubscribe;
    }
  }, [selectedStoreId, user, fetchNotifications, setupRealtimeSubscription]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchNotifications(selectedStoreId);
    await getUnreadCount(selectedStoreId);
    setIsRefreshing(false);
    toast({
      title: t("notifications.refreshed"),
      description: t("notifications.refreshedDescription"),
    });
  };

  const handleMarkAllAsRead = async () => {
    await markAllAsRead(selectedStoreId);
    toast({
      title: t("notifications.allMarkedAsRead"),
      description: t("notifications.allMarkedAsReadDescription"),
    });
  };

  const handleMarkAsRead = async (id: string) => {
    await markAsRead(id);
    toast({
      title: t("notifications.markedAsRead"),
      description: t("notifications.markedAsReadDescription"),
    });
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteNotification(id);
      toast({
        title: t("notifications.deleted"),
        description: t("notifications.deletedDescription"),
      });
    } catch (error) {
      console.error("Failed to delete notification:", error);
      toast({
        title: t("notifications.error"),
        description: "Failed to delete notification. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleNavigate = (notification: SystemNotification) => {
    toast({
      title: t("notifications.navigation"),
      description: t("notifications.navigationDescription"),
    });
  };

  const filteredNotifications = notifications.filter((notification) => {
    if (activeTab === "all") return true;
    if (activeTab === "unread") return notification.status === "unread";
    
    const category = getNotificationCategory(notification.type as NotificationType);
    if (activeTab === "orders") return category === "orders";
    if (activeTab === "staff") return category === "staff";
    if (activeTab === "inventory") return category === "inventory";
    if (activeTab === "reservations") return category === "reservations";
    if (activeTab === "payments") return category === "payments";
    if (activeTab === "system") return category === "system";
    return true;
  });

  if (!selectedStoreId) {
    return (
      <div className="flex items-center justify-center h-64">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t("notifications.noStoreSelected")}</h3>
            <p className="text-gray-600 mb-4">{t("notifications.noStoreSelectedDescription")}</p>
            <Button onClick={() => navigate("/dashboard/stores")}>
              {t("notifications.selectStore")}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NotificationHeader
        unreadCount={unreadCount}
        onMarkAllAsRead={handleMarkAllAsRead}
        onRefresh={handleRefresh}
        isRefreshing={isRefreshing}
      />

      <NotificationTabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        unreadCount={unreadCount}
      >
        <NotificationContent
          isLoading={isLoading}
          error={error}
          notifications={filteredNotifications}
          activeTab={activeTab}
          onMarkAsRead={handleMarkAsRead}
          onDelete={handleDelete}
          onNavigate={handleNavigate}
          onRefresh={handleRefresh}
        />
      </NotificationTabs>
    </div>
  );
};

export default NotificationsPage; 